import * as R from "remeda";

import { safeProcessExport, PipelineArtifacts, PipelineFailure } from "@validator/index";
import { ClaudeManifestSchema, ClaudeManifest, InputFileType } from "@validator/schema";

import { LinkedStudy, SubmissionReportData } from "@shared/types";
import {
  effectiveStudy,
  isNonValidatedLinkedStudy,
  linkedStudyChatMessageExpirationDate,
} from "@shared/study";
import { userMessageCount, userMessagesDateRange } from "@shared/report";

import { AppState, Dispatch } from "@/reducers/app";
import { WriteSubmissionAction } from "@/reducers/submission";
import {
  FileReadError,
  FileReadErrorReason,
  SubmissionFailure,
  SubmissionFailureReason,
  ValidateArtifact,
} from "@/types/submission";

import { SubmissionType } from "@/context/AppContext";

import { logValidationError } from "@/api/alerts";

/* Types ---------------------------------------------------------------------*/

type StageFailure<S extends PipelineFailure["stage"]> = Extract<PipelineFailure, { stage: S }>;

type EmptyArchive = Extract<StageFailure<"extract">["detail"], { status: "empty" }>;

interface LoggedFailure extends Pick<SubmissionFailure, "source" | "errors"> {
  reason: SubmissionFailureReason | FileReadErrorReason;
}

/* Helpers -------------------------------------------------------------------*/

// Schema

export function claudeConversationsUrls(manifest: ClaudeManifest): string[] {
  return R.pipe(
    manifest.data_files,
    R.filter((v) => v.category === "conversations"),
    R.map((v) => v.export_url),
  );
}

// Study

function isNonValidated(state: AppState) {
  return !!state.study.study && isNonValidatedLinkedStudy(state.study.study);
}

// Failures

function logStudyValidationFailure(
  study: LinkedStudy,
  failure: LoggedFailure,
  inputFileType?: InputFileType,
) {
  logValidationError({
    studyKey: study.key,
    studyId: study.id,
    participantId: study.participantId,
    participantCode: study.participantCode,
    timestamp: new Date().toISOString(),
    source: failure.source,
    errors: failure.errors,
    type: failure.reason,
    inputFileType,
  });
}

interface ValidatedRejection {
  result: ValidateArtifact;
  truncated: boolean;
  report: SubmissionReportData;
}

function dispatchValidationError(
  dispatch: Dispatch,
  failure: SubmissionFailure,
  input: File,
  fileType: InputFileType,
  validated: ValidatedRejection | null = null,
) {
  return dispatch({
    type: WriteSubmissionAction.ValidationError,
    message: "Validation error",
    failure,
    file: input,
    fileType,
    result: validated?.result ?? null,
    truncated: validated?.truncated ?? false,
    report: validated?.report ?? null,
  });
}

function rejectSubmission(
  dispatch: Dispatch,
  state: AppState,
  failure: SubmissionFailure,
  input: File,
  fileType: InputFileType,
  validated: ValidatedRejection | null = null,
) {
  if (state.study.study) {
    logStudyValidationFailure(state.study.study, failure, fileType);
  }

  return dispatchValidationError(dispatch, failure, input, fileType, validated);
}

function fromValidateFailure(
  { detail }: StageFailure<"validate">,
  nonValidated: boolean,
): SubmissionFailure {
  if (detail.type === "safe_failure" && detail.data.source !== "unknown") {
    return {
      type: "failure",
      reason: "schema_mismatch",
      overridable: true,
      source: detail.data.source,
      errors: detail.errors,
    };
  }

  return {
    type: "failure",
    reason: "unknown_source",
    overridable: nonValidated,
    source: detail.data.source,
    errors: detail.errors,
  };
}

/* Process Export Result Handlers --------------------------------------------*/

function onEmptyArchive(dispatch: Dispatch, state: AppState, { input, message }: EmptyArchive) {
  const emptyFailure: SubmissionFailure = {
    type: "failure",
    reason: "archive_empty",
    overridable: isNonValidated(state),
    source: "unknown",
    errors: [message],
  };

  return rejectSubmission(dispatch, state, emptyFailure, input, "zip");
}

function onParseFailure(dispatch: Dispatch, state: AppState, failure: StageFailure<"parse">) {
  const { input, inputFileType } = failure.prior.extract;

  const parseFailure: SubmissionFailure = {
    type: "failure",
    reason: "json_parse_failed",
    overridable: inputFileType === "zip" && isNonValidated(state),
    source: "unknown",
    errors: [
      inputFileType === "zip"
        ? "Failed to parse JSON extracted from archive"
        : "Failed to parse imported JSON",
      failure.message,
    ],
  };

  return rejectSubmission(dispatch, state, parseFailure, input, inputFileType);
}

function onValidateFailure(
  dispatch: Dispatch,
  state: AppState,
  submissionType: SubmissionType,
  failure: StageFailure<"validate">,
) {
  const { input, inputFileType } = failure.prior.extract;

  if (submissionType === "public") {
    const validateFailure = fromValidateFailure(failure, false);

    return dispatchValidationError(dispatch, validateFailure, input, inputFileType);
  }

  const study = state.study.study;

  if (!study) {
    return;
  }

  const manifestResult = ClaudeManifestSchema.safeParse(failure.prior.parse);

  if (manifestResult.success) {
    const conversationsUrls = claudeConversationsUrls(manifestResult.data);

    if (conversationsUrls.length === 0) {
      const missingFailure: SubmissionFailure = {
        type: "failure",
        reason: "claude_conversations_missing",
        overridable: false,
        source: "claude",
        errors: ["Missing Claude conversations URLs"],
      };

      return rejectSubmission(dispatch, state, missingFailure, input, inputFileType);
    }

    return dispatch({
      type: WriteSubmissionAction.FoundClaudeManifest,
      manifest: manifestResult.data,
      conversationsUrls,
    });
  }

  const validateFailure = fromValidateFailure(failure, isNonValidatedLinkedStudy(study));

  return rejectSubmission(dispatch, state, validateFailure, input, inputFileType);
}

function onPostValidateFailure(
  dispatch: Dispatch,
  state: AppState,
  failure: StageFailure<"truncate" | "encode">,
) {
  const { input, inputFileType } = failure.prior.extract;

  const submissionFailure: SubmissionFailure = {
    type: "failure",
    reason: "processing_failed",
    overridable: false,
    source: failure.prior.validate.data.source,
    errors: [`Failed to ${failure.stage} chat log`, failure.message],
  };

  return rejectSubmission(dispatch, state, submissionFailure, input, inputFileType);
}

interface ThresholdRule {
  threshold: number;
  count: number;
  suffix: string;
}

function thresholdRule(
  perWeekThreshold: number | undefined,
  staticThreshold: number | undefined,
  windowDays: number,
): ThresholdRule | null {
  if (perWeekThreshold) {
    const threshold = Math.max(1, Math.floor((windowDays / 7) * perWeekThreshold));
    return { threshold, count: perWeekThreshold, suffix: " per week" };
  }

  if (staticThreshold) {
    return { threshold: staticThreshold, count: staticThreshold, suffix: "" };
  }

  return null;
}

function resolveMessageVolumeFailure(
  study: LinkedStudy,
  { validate, truncate }: PipelineArtifacts,
): SubmissionFailure | null {
  const mainStudy = effectiveStudy(study);
  const expiresAt = linkedStudyChatMessageExpirationDate(study);
  const windowDays = mainStudy.baselineChatHistoryWindowDays ?? null;
  const lowMessageVolume = mainStudy.flags?.lowMessageVolume;

  if (!expiresAt || windowDays === null || !lowMessageVolume?.enabled) {
    return null;
  }

  const { perWeekThreshold, staticThreshold } = lowMessageVolume;
  const rule = thresholdRule(perWeekThreshold, staticThreshold, windowDays);

  if (!rule || userMessageCount(truncate.data) >= rule.threshold) {
    return null;
  }

  const msgStr = rule.count === 1 ? "message" : "messages";

  return {
    type: "failure",
    reason: "low_message_volume",
    overridable: true,
    source: validate.data.source,
    errors: [`Less than ${rule.count} ${msgStr}${rule.suffix} over the past ${windowDays} days`],
  };
}

function onProcessed(
  dispatch: Dispatch,
  state: AppState,
  submissionType: SubmissionType,
  artifacts: PipelineArtifacts,
) {
  const { extract, validate, truncate } = artifacts;
  if (submissionType === "public") {
    return dispatch({
      type: WriteSubmissionAction.Validated,
      result: validate,
      file: extract.extracted,
      truncated: false,
      report: null,
    });
  }

  const study = state.study.study;

  if (!study) {
    return;
  }

  const { input, inputFileType } = extract;

  if (truncate.data.content.length === 0) {
    const emptyFailure: SubmissionFailure = {
      type: "failure",
      reason: "empty_after_truncation",
      overridable: true,
      source: validate.data.source,
      errors: ["Conversations empty after truncation"],
    };

    return rejectSubmission(dispatch, state, emptyFailure, input, inputFileType);
  }

  const truncated = linkedStudyChatMessageExpirationDate(study) !== null;
  const messageCount = userMessageCount(truncate.data);
  const [oldestUserMessageAt, newestUserMessageAt] = userMessagesDateRange(truncate.data) || [
    null,
    null,
  ];

  const report: SubmissionReportData = {
    userMessageCount: messageCount,
    oldestUserMessageAt: oldestUserMessageAt?.toISOString() ?? null,
    newestUserMessageAt: newestUserMessageAt?.toISOString() ?? null,
  };

  const lowVolumeFailure = resolveMessageVolumeFailure(study, artifacts);

  if (lowVolumeFailure) {
    return rejectSubmission(dispatch, state, lowVolumeFailure, extract.extracted, inputFileType, {
      result: truncate,
      truncated,
      report,
    });
  }

  dispatch({
    type: WriteSubmissionAction.Validated,
    result: truncate,
    file: extract.extracted,
    truncated,
    report,
  });
}

/* Main functions ------------------------------------------------------------*/

export function onReadFileError(
  dispatch: Dispatch,
  state: AppState,
  { message, reason = "file_read_failed", log = true }: FileReadError,
) {
  if (state.study.study && log) {
    logStudyValidationFailure(state.study.study, {
      reason,
      source: "unknown",
      errors: [message],
    });
  }

  dispatch({ type: WriteSubmissionAction.FileError, message });
}

export async function onReadFile(
  dispatch: Dispatch,
  state: AppState,
  submissionType: SubmissionType,
  file: File,
) {
  const study = state.study.study;

  if (submissionType === "study" && !study) {
    return;
  }

  dispatch({ type: WriteSubmissionAction.Validating });

  const truncateBefore =
    submissionType === "study" && study
      ? (linkedStudyChatMessageExpirationDate(study) ?? undefined)
      : undefined;

  let result: Awaited<ReturnType<typeof safeProcessExport>>;

  try {
    result = await safeProcessExport(file, { truncateBefore });
  } catch (e) {
    return onReadFileError(dispatch, state, {
      message: e instanceof Error ? e.message : "Could not read file",
    });
  }

  if (result.ok) {
    return onProcessed(dispatch, state, submissionType, result.artifacts);
  }

  const { failure } = result;

  switch (failure.stage) {
    case "extract":
      if (failure.detail.status === "empty") {
        return onEmptyArchive(dispatch, state, failure.detail);
      }
      return onReadFileError(dispatch, state, {
        message: failure.detail.message,
        reason: failure.detail.reason,
        log: failure.detail.reason !== "unsupported_file",
      });
    case "parse":
      return onParseFailure(dispatch, state, failure);
    case "validate":
      return onValidateFailure(dispatch, state, submissionType, failure);
    case "truncate":
    case "encode":
      return onPostValidateFailure(dispatch, state, failure);
  }
}
